<script setup lang="ts">
import { computed, inject, onMounted } from 'vue';
import { useQuasar } from 'quasar';
import { useRouter } from 'vue-router';
import DashboardCard from '../../components/DashboardDetailsCard/DashboardDetailsCard.vue';
import DashboardSalutationBanner from '../../components/DashboardSalutationBanner/DashboardSalutationBanner.vue';
import { useAnalytics } from 'src/modules/Analytics/composables/useAnalytics';
import { hasNoActiveEnrollment } from 'src/shared/composables/useStrapiApi';
import DashboardRoleAggregateCards from '../../components/DashboardRoleAggregateCards/DashboardRoleAggregateCards.vue';
import DashboardStudentClassList from '../../components/DashboardStudentClassList/DashboardStudentClassList.vue';
import { useStudentSemesterOverview } from 'src/modules/School/composables/useStudentSemesterOverview';
import StreakCard from 'src/modules/Analytics/components/StreakCard/StreakCard.vue';
import StatSummaryCard from 'src/modules/Analytics/components/StatSummaryCard/StatSummaryCard.vue';
import TotalTimeCard from 'src/modules/Analytics/components/TotalTimeCard/TotalTimeCard.vue';
import AverageScoreCard from 'src/modules/Analytics/components/AverageScoreCard/AverageScoreCard.vue';
import CategoryBreakdownCard from 'src/modules/Analytics/components/CategoryBreakdownCard/CategoryBreakdownCard.vue';
import RecentActivityList from 'src/modules/Analytics/components/RecentActivityList/RecentActivityList.vue';
import type { Ref, ComputedRef } from 'vue';
import type { UserRole } from 'src/types/User';
import type {
  AnalyticsSummary,
  ParentAnalyticsSummary,
  TeacherAnalyticsSummary,
} from 'src/modules/Analytics/types';

const invalidLicense = inject('invalidLicense') as Ref<boolean>;
const currentUserRole = inject('currentUserRole') as ComputedRef<
  UserRole | undefined
>;
const $q = useQuasar();
const router = useRouter();
const {
  summary,
  loading: analyticsLoading,
  error: analyticsError,
  fetchMySummary,
  fetchParentSummary,
  fetchTeacherSummary,
} = useAnalytics();

const isParent = computed(() => currentUserRole?.value === 'parent');
const isTeacher = computed(() => currentUserRole?.value === 'teacher');
const isChild = computed(() => currentUserRole?.value === 'child');
const isStudent = computed(() => currentUserRole?.value === 'student');

// A student with no active enrollment is awaiting the teacher's approval (the
// enrollment-gated endpoints return 403). There is nothing to chart yet, so the
// analytics section is hidden and the scary error toast is suppressed centrally;
// the class card's status chip carries the explanation.
const isPendingApproval = computed(
  () => isStudent.value && hasNoActiveEnrollment.value,
);

/*
 * Deliberately independent of the drawer's filters: a student sees every semester
 * they can be enrolled in (current + what's coming), not a slice chosen by a picker
 * they never touched. See useStudentSemesterOverview.
 */
const {
  groups: studentSemesterGroups,
  loading: studentClassesLoading,
  loaded: studentClassesLoaded,
  fetchOverview,
} = useStudentSemesterOverview();

const childSummary = computed<AnalyticsSummary | null>(() => {
  const value = summary.value;
  if (!value) return null;
  if ('streak' in value) return value;
  return null;
});

const parentSummary = computed<ParentAnalyticsSummary | null>(() => {
  const value = summary.value;
  if (!value) return null;
  const roleSummary = value as unknown as ParentAnalyticsSummary;
  if (roleSummary.role === 'parent') {
    return roleSummary;
  }
  return null;
});

const teacherSummary = computed<TeacherAnalyticsSummary | null>(() => {
  const value = summary.value;
  if (!value) return null;
  const roleSummary = value as unknown as TeacherAnalyticsSummary;
  if (roleSummary.role === 'teacher') {
    return roleSummary;
  }
  return null;
});

onMounted(async () => {
  if (isStudent.value) {
    void fetchOverview();
  }

  if (isParent.value) {
    await fetchParentSummary();
    return;
  }

  if (isTeacher.value) {
    await fetchTeacherSummary();
    return;
  }

  await fetchMySummary();
});
</script>

<template>
  <div>
    <DashboardSalutationBanner
      :streak="childSummary?.streak"
      :longest-streak="childSummary?.longestStreak"
    />

    <!--
      Rendered outside the pending/approved branches on purpose: a student whose
      enrollment is still pending has no analytics to show, but the class cards
      are exactly what explains why — the "Pending" chip opens the guidance.
    -->
    <DashboardStudentClassList
      v-if="isStudent && !invalidLicense"
      :groups="studentSemesterGroups"
      :loading="studentClassesLoading || !studentClassesLoaded"
    />

    <!-- A student awaiting approval has no analytics to show; the class card's
         status chip (and its dialog) is the only explanation needed. -->
    <template v-if="!isPendingApproval">
      <DashboardCard v-if="!invalidLicense" />

      <q-card
        v-if="!invalidLicense"
        flat
        class="q-pa-sm bg-transparent"
      >
      <q-inner-loading :showing="analyticsLoading" />

      <q-banner
        v-if="analyticsError"
        class="bg-negative text-white q-mb-md"
      >
        {{ analyticsError }}
      </q-banner>

      <template v-if="summary">
        <DashboardRoleAggregateCards
          v-if="isParent && parentSummary"
          role="parent"
          :summary="parentSummary"
        />

        <DashboardRoleAggregateCards
          v-else-if="isTeacher && teacherSummary"
          role="teacher"
          :summary="teacherSummary"
        />

        <div
          v-else-if="isChild && childSummary"
          class="row q-col-gutter-md q-mb-md"
        >
          <div class="col-12 col-sm-6 col-md-4">
            <StreakCard
              :streak="childSummary.streak"
              :longest-streak="childSummary.longestStreak"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4">
            <StatSummaryCard
              :completed="childSummary.completed"
              :incomplete="childSummary.incomplete"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-4">
            <TotalTimeCard :total-time-ms="childSummary.totalTimeMs" />
          </div>
        </div>

        <q-expansion-item
          dense
          expand-separator
          default-opened
          :header-class="$q.dark.isActive ? 'text-white' : 'text-dark'"
          :label="$t('RECENT_ACTIVITY')"
          icon="mdi-chart-line"
          :class="[
            'dashboard-analytics-expansion',
            $q.dark.isActive
              ? 'dashboard-analytics-expansion--dark'
              : 'dashboard-analytics-expansion--light',
          ]"
        >
          <div
            v-if="summary"
            class="row q-col-gutter-md q-mt-sm"
          >
            <div class="col-12 col-md-4">
              <AverageScoreCard :average-score="summary.averageScore" />
            </div>
            <div class="col-12 col-md-4">
              <CategoryBreakdownCard :category-breakdown="summary.categoryBreakdown" />
            </div>
            <div class="col-12 col-md-4">
              <RecentActivityList :recent-activity="summary.recentActivity" />
            </div>
          </div>
        </q-expansion-item>
      </template>
      </q-card>
    </template>

    <q-banner
      v-if="invalidLicense"
      class="bg-negative text-white"
    >
      {{ $t('LICENSE_REQUIRED_REDIRECT_COURSES') }}
      <template #action>
        <q-btn
          flat
          color="white"
          :label="$t('GO_TO_STORE')"
          @click="router.push({ name: 'courses' })"
        />
      </template>
    </q-banner>
  </div>
</template>

<style scoped>
.dashboard-analytics-expansion {
  border-radius: 12px;
}

.dashboard-analytics-expansion--dark {
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.dashboard-analytics-expansion--light {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
